// useOffline hook - Detect network status and manage offline queue
import { useState, useEffect, useCallback } from 'react';

// IndexedDB helpers
const DB_NAME = 'yubi-offline';
const DB_VERSION = 1;
const STORE_NAME = 'actions';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
}

export function useOffline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Update online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger background sync when coming back online
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(async (registration) => {
          if (registration && 'sync' in registration) {
            try {
              await registration.sync.register('sync-offline-actions');
            } catch (err) {
              // Background sync may be disabled or not supported - this is fine
              console.log('Background sync not available:', err.message);
            }
          }
        }).catch((err) => {
          console.log('Service worker not ready:', err);
        });
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for sync complete messages from service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'SYNC_COMPLETE') {
          setLastSyncTime(new Date());
          loadPendingCount();
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  // Load pending actions count
  const loadPendingCount = useCallback(async () => {
    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();
      
      request.onsuccess = () => {
        setPendingActions(request.result);
      };
    } catch (error) {
      console.log('Error loading pending count:', error);
    }
  }, []);

  useEffect(() => {
    loadPendingCount();
  }, [loadPendingCount]);

  // Queue an action for later sync
  const queueAction = useCallback(async (action) => {
    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const actionData = {
        ...action,
        timestamp: Date.now(),
        status: 'pending'
      };
      
      await new Promise((resolve, reject) => {
        const request = store.add(actionData);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      loadPendingCount();
      return true;
    } catch (error) {
      console.error('Error queuing action:', error);
      return false;
    }
  }, [loadPendingCount]);

  // Clear all pending actions
  const clearQueue = useCallback(async () => {
    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
      
      setPendingActions(0);
    } catch (error) {
      console.error('Error clearing queue:', error);
    }
  }, []);

  // Manually trigger sync
  const triggerSync = useCallback(async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        if ('sync' in registration) {
          await registration.sync.register('sync-offline-actions');
        }
      } catch (error) {
        console.error('Error triggering sync:', error);
      }
    }
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    pendingActions,
    lastSyncTime,
    queueAction,
    clearQueue,
    triggerSync,
    refreshPendingCount: loadPendingCount
  };
}

// Hook for caching API data locally
export function useOfflineData(key, fetcher, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const { isOnline } = useOffline();

  const STORAGE_KEY = `yubi_cache_${key}`;

  // Load cached data
  useEffect(() => {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        const { data: cachedData, timestamp } = JSON.parse(cached);
        setData(cachedData);
        
        // Check if data is stale (older than 5 minutes)
        const age = Date.now() - timestamp;
        setIsStale(age > (options.maxAge || 5 * 60 * 1000));
      } catch (e) {
        console.error('Error parsing cached data:', e);
      }
    }
    setLoading(false);
  }, [STORAGE_KEY, options.maxAge]);

  // Fetch fresh data when online
  useEffect(() => {
    if (!isOnline || !fetcher) return;

    const fetchData = async () => {
      try {
        const freshData = await fetcher();
        setData(freshData);
        setIsStale(false);
        setError(null);
        
        // Cache the data
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          data: freshData,
          timestamp: Date.now()
        }));
      } catch (err) {
        setError(err);
        // Keep showing cached data on error
      }
    };

    fetchData();
  }, [isOnline, fetcher, STORAGE_KEY]);

  const refresh = useCallback(async () => {
    if (!fetcher || !isOnline) return;
    
    setLoading(true);
    try {
      const freshData = await fetcher();
      setData(freshData);
      setIsStale(false);
      setError(null);
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        data: freshData,
        timestamp: Date.now()
      }));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fetcher, isOnline, STORAGE_KEY]);

  return { data, loading, error, isStale, refresh };
}

export default useOffline;
